import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

// Type definitions based on the API response
interface Badge {
  url: string;
  title: string;
}

interface Colors {
  primary: string;
}

interface Translation {
  [language: string]: string;
}

interface Section {
  id: string;
  title: string;
  iconUrl: string;
  titleTrans: Translation;
  descriptionTrans: Translation;
}

interface ScarcityTimer {
  enabled: boolean;
  messageTrans: Translation;
  duration: number;
}

interface ComputedConfig {
  availableShippingCountries: string[];
  clubProductId: string;
  threedsEnabled: boolean;
}

export interface StoreConfig {
  badges: Badge[];
  colors: Colors;
  sections: Section[];
  checkoutType: string;
  requirePhone: boolean;
  requireTerms: boolean;
  scarcityTimer: ScarcityTimer;
  showPoweredBy: boolean;
  precheckMarketing: boolean;
  privacyPolicyLink: string;
  requireAddressNumber: boolean;
  generalConditionsLink: string;
  computed: ComputedConfig;
  countryRestrictionsFromShippingRates: boolean;
  stickyPaymentButton: boolean;
  brandLogo: string;
  favicon: string;
}

interface UseStoreConfigOptions {
  autoFetch?: boolean; // If false, won't fetch automatically on mount
}

interface UseStoreConfigReturn {
  config: StoreConfig | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

const API_URL = `${import.meta.env.VITE_API_URL || 'https://api.tagada.io'}/api/v1/checkout/config`;

export const useStoreConfig = (options: UseStoreConfigOptions = {}): UseStoreConfigReturn => {
  const { id } = useParams<{ id: string }>();

  if (!id) {
    throw new Error('Store id is not defined');
  }

  const { autoFetch = true } = options;

  const [config, setConfig] = useState<StoreConfig | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchConfig = async () => {
    setLoading(true);
    setError(null);

    try {
      const url = `${API_URL}?storeId=${encodeURIComponent(id)}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch store config: ${response.status} ${response.statusText}`);
      }

      const data: StoreConfig = await response.json();
      setConfig(data);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('An unknown error occurred');
      setError(error);
      console.error('Error fetching store config:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoFetch) {
      fetchConfig();
    }
     
  }, [id, autoFetch]);

  return {
    config,
    loading,
    error,
    refetch: fetchConfig,
  };
};

// Export types for use in other components
export type { Badge, Colors, ComputedConfig, ScarcityTimer, Section, Translation };
